import useRequest from "@/lib/request/useRequest";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
    NitrogenPlan,
    NitrogenPlanSimulation,
    ProcessingStatus,
    SimulationStatistics,
} from "@/types/fieldNitrogenPlan";
import { Fertilizer } from "@/types/fertilizer";
import { NitrogenSimulationType } from "@/types/enums";
import { SortingState } from "@tanstack/react-table";
import dayjs from "dayjs";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    ButtonGroup,
    Chip,
    ModalBody,
    ModalHeader,
} from "@heroui/react";
import TableV2 from "@/components/table/TableV2";
import { roundTo } from "@/lib/utils/numbers";
import {
    getSimulationTypeLabel,
    getSimulationTypeSortValue,
} from "@/lib/utils/nitrogenPlan";
import InfoModal from "../common/InfoModal";
import {
    faInfoCircle,
    faRotate,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Field } from "@/types/field";
import { Key } from "@react-types/shared";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import useAuth from "@/lib/auth/useAuth";
import { sortArrayByKey } from "@/lib/utils/sorting";

type TableType = NitrogenPlanSimulation & {
    penalty_early_CWAD_ranking: number;
    penalty_NIAD_ranking: number;
    penalty_remob_ranking: number;
    penalty_roi_ranking: number;
    penalty_early_NUPC_ranking: number;
};

function getRankings(
    simulations: NitrogenPlanSimulation[],
    key: (typeof penaltyKeys)[number],
) {
    const values = simulations.map((sim, idx) => ({
        idx,
        value: sim[key] ?? Infinity,
    }));
    values.sort((a, b) => a.value - b.value);
    const rankings = Array(simulations.length).fill(0);
    let rank = 1;
    for (let i = 0; i < values.length; i++) {
        if (i > 0 && values[i].value !== values[i - 1].value) {
            rank = i + 1;
        }
        rankings[values[i].idx] = rank;
    }
    return rankings;
}

function getRedGreenGradient(value: number): string {
    const colors = [
        "#2ecc4033",
        "#b6e64933",
        "#ffd92b33",
        "#ffae4233",
        "#ff704333",
        "#ff413633",
    ];
    const idx = Math.max(0, Math.min(5, value - 1));
    return colors[idx];
}

const CHART_KEYS = [
    { key: "nitrogen_uptake", label: "N Uptake" },
    { key: "CWAD", label: "CWAD" },
    {
        key: "soil_residual_nitrogen_surplus",
        label: "Soil Residual Nitrogen Surplus",
    },
    { key: "remobilization", label: "Remobilization" },
];

function RankingCell({ value }: { value: number }) {
    const color = getRedGreenGradient(value);
    return (
        <div
            style={{ backgroundColor: color }}
            className="w-10 h-10 bg-opacity-25 flex flex-row justify-center items-center text-xl font-semibold rounded-md"
        >
            {value}
        </div>
    );
}

function getColor(simulationType: NitrogenSimulationType) {
    return {
        [NitrogenSimulationType.Sentinel]: "#2ecc40",
        [NitrogenSimulationType.Decadal90]: "#ff4136",
        [NitrogenSimulationType.Decadal50]: "#ff851b",
        [NitrogenSimulationType.Wet]: "#0074d9",
        [NitrogenSimulationType.No_Rate]: "#111111",
        [NitrogenSimulationType.Max_Rate]: "#777",
        [NitrogenSimulationType.Drought]: "#ffdc00",
    }[simulationType];
}

const penaltyKeys = [
    "penalty_early_CWAD",
    "penalty_NIAD",
    "penalty_remob",
    "penalty_roi",
    "penalty_early_NUPC",
] as const;

function FieldNitrogenAdvisor({
    fieldId,
    onClose,
    hideCloseButton = false,
}: {
    fieldId: string;
    onClose: () => void;
    hideCloseButton?: boolean;
}) {
    const { userIsSentinel } = useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();

    const getFertilizers = useGetRequest<Fertilizer[]>({
        url: "/fertilizers/",
    });

    const fieldQuery = useGetRequest<Field>({
        url: `/fields/${fieldId}/`,
        enabled: !!fieldId,
    });
    const [selectedProductId, setSelectedProductId] = useState<Key | null>(
        null,
    );
    useEffect(() => {
        if (fieldQuery.data) {
            setSelectedProductId(
                // 32% UAN = ID 2
                fieldQuery.data.default_product?.toString() ?? "2",
            );
        }
    }, [fieldQuery.data]);

    const today = useMemo(() => getTodayWithTimeTravel(), []);
    const plan = useGetRequest<NitrogenPlan>({
        url: "/field_nitrogen_plans/query/",
        params: {
            field_id: fieldId,
            date: today.format("YYYY-MM-DD"),
            product_id: selectedProductId,
        },
        enabled: !!selectedProductId && !!fieldId,
    });

    const statistics = useGetRequest<SimulationStatistics[]>({
        url: `/field_nitrogen_plans/${plan.data?.id}/statistics/`,
        params: {
            product_id: selectedProductId,
        },
        enabled: !!plan.data?.id,
    });

    const generatePlan = useMutateRequest({
        url: "/field_nitrogen_plans/generate/",
        method: "put",
        errorMessage: "Failed to generate plan",
        successMessage: "Plan generated successfully",
    });

    const fertilizersById = useMemo(() => {
        return (
            getFertilizers.data?.reduce(
                (acc, fertilizer) => {
                    acc[fertilizer.id] = fertilizer;
                    return acc;
                },
                {} as Record<number, Fertilizer>,
            ) || {}
        );
    }, [getFertilizers.data]);

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: "simulation_type",
            desc: false,
        },
    ]);

    const tableData: TableType[] = useMemo(() => {
        const simulations = plan.data?.simulations.filter(
            (simulation) =>
                simulation.simulation_type !== NitrogenSimulationType.Max_Rate,
        );
        if (!simulations) return [];
        const rankingsByPenalty: Record<string, number[]> = {};
        for (const key of penaltyKeys) {
            rankingsByPenalty[key] = getRankings(simulations, key);
        }

        return simulations.map((simulation, i) => {
            return {
                ...simulation,
                penalty_early_CWAD_ranking:
                    rankingsByPenalty["penalty_early_CWAD"][i],
                penalty_NIAD_ranking: rankingsByPenalty["penalty_NIAD"][i],
                penalty_remob_ranking: rankingsByPenalty["penalty_remob"][i],
                penalty_roi_ranking: rankingsByPenalty["penalty_roi"][i],
                penalty_early_NUPC_ranking:
                    rankingsByPenalty["penalty_early_NUPC"][i],
            };
        });
    }, [plan.data?.simulations]);

    const columns: ColumnDef<TableType>[] = [
        {
            id: "simulation_type",
            header: "Type",
            accessorFn: (row) => getSimulationTypeLabel(row.simulation_type),
            size: 150,
            cell: ({ row }) => {
                const color = getColor(row.original.simulation_type);
                return (
                    <div className="flex flex-row items-center gap-2">
                        <div
                            className="rounded-md w-5 h-5 flex items-center justify-center "
                            style={{ backgroundColor: color }}
                        ></div>
                        <span className="text-base font-semibold">
                            {getSimulationTypeLabel(
                                row.original.simulation_type,
                            )}
                        </span>
                    </div>
                );
            },
            sortingFn: (a, b) => {
                return (
                    getSimulationTypeSortValue(a.original.simulation_type) -
                    getSimulationTypeSortValue(b.original.simulation_type)
                );
            },
        },
        {
            id: "rate",
            header: "Rate",
            accessorFn: (row) => roundTo(row.current_rate, 0),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col items-start ">
                        <span className="text-xl font-semibold">
                            {roundTo(row.original.current_rate, 0)}
                        </span>
                        <span className="text-xs text-gray-500">
                            ({roundTo(row.original.minimum_rate, 0)} -{" "}
                            {roundTo(row.original.maximum_rate, 0)})
                        </span>
                    </div>
                );
            },
            size: 100,
        },
        {
            id: "Performance Rankings",
            header: () => {
                return (
                    <div>
                        <InfoModal
                            trigger={(setShowModal) => (
                                <div className="flex flex-row items-center  w-full gap-2">
                                    Performance Rankings
                                    <Button
                                        onPress={() => setShowModal(true)}
                                        size="sm"
                                        variant="light"
                                        color="primary"
                                        isIconOnly
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                    </Button>
                                </div>
                            )}
                        >
                            <ModalHeader className="font-semibold text-xl">
                                Penalties
                            </ModalHeader>
                            <ModalBody>
                                <p>
                                    The penalties are calculated based on the
                                    following criteria:
                                </p>
                            </ModalBody>
                        </InfoModal>
                    </div>
                );
            },
            size: 500,
            columns: [
                {
                    id: "penalty_early_CWAD_ranking",
                    header: "Early CWAD",
                    accessorFn: (row) => row.penalty_early_CWAD_ranking,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_early_CWAD_ranking}
                        />
                    ),
                    size: 100,
                },
                {
                    id: "penalty_NIAD_ranking",
                    header: "NIAD",
                    accessorFn: (row) => row.penalty_NIAD_ranking,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_NIAD_ranking}
                        />
                    ),
                    size: 100,
                },
                {
                    id: "penalty_remob_ranking",
                    header: "Remob",
                    accessorFn: (row) => row.penalty_remob_ranking,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_remob_ranking}
                        />
                    ),
                    size: 100,
                },
                {
                    id: "penalty_roi_ranking",
                    header: "ROI",
                    accessorFn: (row) => row.penalty_roi_ranking,
                    size: 100,
                    cell: ({ row }) => (
                        <RankingCell value={row.original.penalty_roi_ranking} />
                    ),
                },
                {
                    id: "penalty_early_NUPC_ranking",
                    header: "Early NUPC",
                    accessorFn: (row) => row.penalty_early_NUPC_ranking,
                    size: 100,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_early_NUPC_ranking}
                        />
                    ),
                },
            ],
        },
    ];

    const chartData = useMemo(() => {
        if (!plan.data || !statistics.data) return [];
        const maxRateSimulation = plan.data?.simulations.find(
            (simulation) =>
                simulation.simulation_type === NitrogenSimulationType.Max_Rate,
        );
        const simulationTypes = plan.data?.simulations.reduce(
            (acc, simulation) => {
                acc[simulation.id] = simulation;
                return acc;
            },
            {} as Record<number, NitrogenPlanSimulation>,
        );

        if (!maxRateSimulation || !simulationTypes) return [];

        const maxLookup = statistics.data
            ?.filter((stat) => stat.simulation === maxRateSimulation.id)
            .reduce(
                (acc, stat) => {
                    acc[stat.date] = {
                        date: stat.date,
                        nitrogen_uptake: stat.nitrogen_uptake,
                        nitrogen_applied: stat.nitrogen_applied,
                        CWAD: stat.CWAD,
                        NUPC: stat.NUPC,
                        soil_residual_nitrogen_surplus:
                            stat.soil_residual_nitrogen_surplus,
                        remobilization: stat.remobilization,
                    };
                    return acc;
                },
                {} as Record<
                    string,
                    {
                        date: string;
                        nitrogen_uptake: number;
                        nitrogen_applied: number;
                        CWAD: number;
                        NUPC: number;
                        soil_residual_nitrogen_surplus: number;
                        remobilization: number;
                    }
                >,
            );

        if (Object.keys(maxLookup).length === 0) return [];

        return sortArrayByKey(
            Object.values(
                statistics.data.reduce(
                    (acc, stat) => {
                        const simulationType =
                            simulationTypes[stat.simulation]?.simulation_type;
                        const updatedData = {
                            date: stat.date,
                            [`nitrogen_uptake__${simulationType}`]:
                                stat.nitrogen_uptake /
                                maxLookup[stat.date].nitrogen_uptake,
                            [`nitrogen_applied__${simulationType}`]:
                                stat.nitrogen_applied /
                                maxLookup[stat.date].nitrogen_applied,
                            [`CWAD__${simulationType}`]:
                                stat.CWAD / maxLookup[stat.date].CWAD,
                            [`NUPC__${simulationType}`]:
                                stat.NUPC / maxLookup[stat.date].NUPC,
                            [`soil_residual_nitrogen_surplus__${simulationType}`]:
                                stat.soil_residual_nitrogen_surplus,
                            [`remobilization__${simulationType}`]:
                                stat.remobilization,
                        };
                        acc[stat.date] = { ...acc[stat.date], ...updatedData };
                        return acc;
                    },
                    {} as Record<string, Record<string, number | string>>,
                ),
            ),
            "date",
        );
    }, [plan.data, statistics.data]);

    const [selectedChart, setSelectedChart] =
        useState<string>("nitrogen_uptake");

    const advisorAvailable = useMemo(() => {
        const allTypes = Object.values(NitrogenSimulationType);
        const presentTypes = (plan.data?.simulations ?? []).map(
            (simulation) => simulation.simulation_type,
        );
        return (
            allTypes.every((type) => presentTypes.includes(type)) &&
            plan.data?.simulations.every(
                (simulation) =>
                    simulation.processing_status === ProcessingStatus.COMPLETED,
            )
        );
    }, [plan.data?.simulations]);

    const advisorFailed = useMemo(() => {
        return plan.data?.simulations.some(
            (simulation) =>
                simulation.processing_status === ProcessingStatus.FAILED,
        );
    }, [plan.data?.simulations]);

    const advisorUngenerated = useMemo(() => {
        return (plan.data?.simulations ?? []).length === 0;
    }, [plan.data?.simulations]);

    const generate = useCallback(() => {
        generatePlan.mutate(
            {
                data: {
                    product: Number(selectedProductId),
                    field: Number(fieldId),
                    date: today.format("YYYY-MM-DD"),
                },
            },
            {
                onSuccess: () => {
                    plan.refetch();
                    statistics.refetch();
                },
            },
        );
    }, [generatePlan, selectedProductId, fieldId, today, plan, statistics]);

    const firstSimulation = useMemo(() => {
        return plan.data?.simulations.find(
            (simulation) =>
                simulation.simulation_type === NitrogenSimulationType.Sentinel,
        );
    }, [plan.data?.simulations]);

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row justify-between items-center">
                <div className="text-xl font-bold">Sentinel Advisor</div>
                {!hideCloseButton && (
                    <Button
                        onPress={onClose}
                        size="sm"
                        variant="light"
                        color="primary"
                        isIconOnly
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </Button>
                )}
            </div>

            <div className="flex flex-row justify-between gap-2 items-end rounded-md py-2">
                <div>
                    <Autocomplete
                        selectedKey={selectedProductId as Key}
                        onSelectionChange={setSelectedProductId}
                        label="Product"
                        labelPlacement="outside"
                        placeholder="Select Product"
                        size="sm"
                        isDisabled={generatePlan.isPending}
                    >
                        {Object.values(fertilizersById).map((fertilizer) => (
                            <AutocompleteItem key={fertilizer.id.toString()}>
                                {fertilizer.name}
                            </AutocompleteItem>
                        ))}
                    </Autocomplete>
                </div>

                {advisorAvailable && (
                    <div>
                        <Button
                            onPress={generate}
                            size="sm"
                            color="primary"
                            isLoading={generatePlan.isPending}
                            isDisabled={generatePlan.isPending}
                            startContent={<FontAwesomeIcon icon={faRotate} />}
                        >
                            Regenerate Advisor
                        </Button>
                    </div>
                )}
            </div>
            {(advisorUngenerated || advisorFailed) && (
                <div className="flex flex-col justify-between gap-4 items-center rounded-md py-2">
                    {advisorUngenerated ? (
                        <div className="text-gray-500 text-sm">
                            Advisor has not been generated for this product
                        </div>
                    ) : (
                        <div className="text-red-500 text-sm">
                            Advisor processing failed
                        </div>
                    )}
                    <Button
                        onPress={generate}
                        size="sm"
                        color="primary"
                        isLoading={generatePlan.isPending}
                        isDisabled={generatePlan.isPending}
                    >
                        Generate Advisor Data
                    </Button>
                </div>
            )}
            {advisorAvailable && (
                <>
                    <div className="flex flex-row ">
                        <ButtonGroup size="sm">
                            {CHART_KEYS.map(({ key, label }) => (
                                <Button
                                    key={key}
                                    color={
                                        selectedChart === key
                                            ? "primary"
                                            : "default"
                                    }
                                    onPress={() => setSelectedChart(key)}
                                >
                                    {label}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart
                            margin={{
                                top: 10,
                                right: 0,
                                left: 0,
                                bottom: 10,
                            }}
                            data={chartData}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                fontSize={12}
                                dataKey="date"
                                type="category"
                                angle={-24}
                                dy={16}
                                tickFormatter={(value) =>
                                    dayjs(value).format("MMM D")
                                }
                            />
                            <YAxis
                                label={{
                                    value:
                                        CHART_KEYS.find(
                                            (key) => key.key === selectedChart,
                                        )?.label ?? "",
                                    angle: -90,
                                    position: "outerLeft",
                                    dx: -15,
                                }}
                                fontSize={12}
                            />
                            {Object.values(NitrogenSimulationType).map(
                                (simulationType) => (
                                    <Line
                                        id={simulationType}
                                        key={simulationType}
                                        dataKey={`${selectedChart}__${simulationType}`}
                                        stroke={getColor(simulationType)}
                                        strokeDasharray={
                                            simulationType ===
                                            NitrogenSimulationType.No_Rate
                                                ? "3 3"
                                                : "0 0"
                                        }
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                ),
                            )}
                            <Tooltip
                                formatter={(value, name) => {
                                    return [
                                        `${roundTo(value as number, 2)}`,
                                        getSimulationTypeLabel(
                                            name
                                                ?.toString()
                                                .split(
                                                    "__",
                                                )[1] as NitrogenSimulationType,
                                        ),
                                    ];
                                }}
                                labelFormatter={(value) =>
                                    dayjs(value).format("MMM D")
                                }
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                    {userIsSentinel && firstSimulation && (
                        <Chip radius="sm" color="secondary">
                            <div className="flex flex-row items-center gap-2">
                                <div className="font-semibold">
                                    Imagery Rate (lbs):
                                </div>
                                <div className="flex flex-row items-end gap-2">
                                    {roundTo(firstSimulation.imagery_rate, 0)}
                                    <span className="">
                                        (
                                        {roundTo(
                                            firstSimulation.imagery_minimum_rate,
                                            0,
                                        )}{" "}
                                        -{" "}
                                        {roundTo(
                                            firstSimulation.imagery_maximum_rate,
                                            0,
                                        )}
                                        )
                                    </span>
                                </div>
                            </div>
                        </Chip>
                    )}
                    <div className=" rounded text-xs">
                        <TableV2
                            data={tableData}
                            columns={columns}
                            sorting={sorting}
                            setSorting={setSorting}
                            manualSorting={false}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

export default FieldNitrogenAdvisor;
